import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { DollarSign } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useRefundReport } from '../../../context/RefundReportContext';
import { useMemo } from 'react';

export const RefundCard = () => {
  const navigate = useNavigate();
  const { tripId } = useParams<{ tripId: string }>();
  const { deductibleExpenses, perDiems, loading } = useRefundReport();

  // Calculate default refund (IL report logic):
  // - Flights: 0 (default)
  // - Accommodations: 0 (default)
  // - Deductible expenses: full amount
  // - Per Diems: total ILS
  const totalRefund = useMemo(() => {
    // Sum all deductible expenses (converted to ILS)
    const deductibleTotal = deductibleExpenses.reduce((sum, expense) => {
      return sum + expense.convertedAmount;
    }, 0);

    // Sum all per diems (in ILS)
    const perDiemTotal = perDiems.reduce((sum, perDiem) => {
      return sum + (perDiem.total_ils || 0);
    }, 0);

    return deductibleTotal + perDiemTotal;
  }, [deductibleExpenses, perDiems]);

  if (loading) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Estimated Refund (IL)</CardTitle>
          <DollarSign className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-8 w-32 mb-2" />
          <Skeleton className="h-4 w-40" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      className="cursor-pointer hover:bg-muted/50 transition-colors"
      onClick={() => navigate(`/trip/${tripId}/reports/refund-report-il`)}
    >
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">Estimated Refund (IL)</CardTitle>
        <DollarSign className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        <div className="text-2xl md:text-3xl font-bold">
          ₪{totalRefund.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          Click to view and customize
        </p>
      </CardContent>
    </Card>
  );
};
